import { Cursors, AUTO, HIT_TEST_DISTANCE } from '../../constants.js';
import '../../math/Graph.js';
import { Rect } from '../../math/Shapes.js';
import { ConnectionEditUndoUnit } from '../ConnectionEditUndoUnit.js';
import { Shape } from '../../dom/Shape.js';
import { deepExtend } from '../../utils/deep-extend.js';
import '@progress/kendo-drawing';
import '../../svg/Rotation.js';
import { Circle } from '../../svg/Circle.js';
import { AdornerBase } from './AdornerBase.js';

class ConnectionEditAdorner extends AdornerBase {
    constructor(connection, options) {
        options = deepExtend({ handles: {} }, options);
        super(connection.diagram, options);
        this.connection = connection;
        const diagram = this.connection.diagram;
        this._ts = diagram.toolService;
        const sp = this.connection.sourcePoint();
        const tp = this.connection.targetPoint();
        this.spVisual = new Circle(deepExtend(this.options.handles, { center: sp }));
        this.epVisual = new Circle(deepExtend(this.options.handles, { center: tp }));
        this.visual.append(this.spVisual);
        this.visual.append(this.epVisual);
    }
    _getCursor() {
        return Cursors.move;
    }
    start(p) {
        this.handle = this._hitTest(p);
        this.startPoint = p;
        this._initialSource = this.connection.source();
        this._initialTarget = this.connection.target();
        switch (this.handle) {
            case -1:
                if (this.connection.targetConnector) {
                    this._ts._connectionManipulation(this.connection, this.connection.targetConnector.shape);
                }
                break;
            case 1:
                if (this.connection.sourceConnector) {
                    this._ts._connectionManipulation(this.connection, this.connection.sourceConnector.shape);
                }
                break;
        }
    }
    move(handle, p) {
        switch (handle) {
            case -1:
                this.connection.source(p);
                break;
            case 1:
                this.connection.target(p);
                break;
            default: {
                const delta = p.minus(this.startPoint);
                this.startPoint = p;
                if (!this.connection.sourceConnector) {
                    this.connection.source(this.connection.sourcePoint().plus(delta));
                }
                if (!this.connection.targetConnector) {
                    this.connection.target(this.connection.targetPoint().plus(delta));
                }
                break;
            }
        }
        this.refresh();
        return true;
    }
    stop(p) {
        const ts = this.diagram.toolService, item = ts.hoveredItem;
        let target;
        if (ts._hoveredConnector) {
            target = ts._hoveredConnector._c;
        }
        else if (item && item instanceof Shape) {
            target = item.getConnector(AUTO) || item.getConnector(p);
        }
        else {
            target = p;
        }
        if (this.handle === -1) {
            this.connection.source(target);
        }
        else if (this.handle === 1) {
            this.connection.target(target);
        }
        this.handle = undefined;
        this._ts._connectionManipulation();
        return new ConnectionEditUndoUnit(this.connection, this._initialSource, this._initialTarget);
    }
    _hitTest(point) {
        const sourcePoint = this.connection.sourcePoint();
        const targetPoint = this.connection.targetPoint();
        const radiusX = this.options.handles.width / 2 + HIT_TEST_DISTANCE;
        const radiusY = this.options.handles.height / 2 + HIT_TEST_DISTANCE;
        const sourcePointDistance = sourcePoint.distanceTo(point);
        const targetPointDistance = targetPoint.distanceTo(point);
        const sourceHandle = new Rect(sourcePoint.x, sourcePoint.y).inflate(radiusX, radiusY).contains(point);
        const targetHandle = new Rect(targetPoint.x, targetPoint.y).inflate(radiusX, radiusY).contains(point);
        let handle = 0;
        if (sourceHandle && (!targetHandle || sourcePointDistance < targetPointDistance)) {
            handle = -1;
        }
        else if (targetHandle && (!sourceHandle || targetPointDistance < sourcePointDistance)) {
            handle = 1;
        }
        return handle;
    }
    refresh() {
        this.spVisual.redraw({ center: this.diagram.modelToLayer(this.connection.sourcePoint()) });
        this.epVisual.redraw({ center: this.diagram.modelToLayer(this.connection.targetPoint()) });
    }
}

export { ConnectionEditAdorner };
